/**
 * CalculatedMeasuresPanel — Inception-to-date Calculated Measures.
 *
 * Lets a modeler (1) designate a canonical fact's as-of / snapshot date roles,
 * (2) define reusable calculated measures (aggregation + AND/OR condition +
 * Period vs Inception-to-date accumulation), (3) link a summary field to a
 * calc measure, (4) evaluate a measure at an as-of date (summary vs canonical
 * substitution), and (5) reconcile stored month-end snapshots against the
 * canonical recompute.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Plus, Trash2, Pencil, PlayCircle, GitCompareArrows, CalendarClock, Check, ChevronsUpDown, Database, Infinity as InfinityIcon } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const AGGS = ['sum', 'count', 'avg', 'min', 'max'];
const OPERATORS = [
  { v: 'equals', l: 'equals' }, { v: 'not_equals', l: 'not equals' },
  { v: 'in', l: 'in list' }, { v: 'not_in', l: 'not in list' },
  { v: 'gt', l: '>' }, { v: 'gte', l: '≥' }, { v: 'lt', l: '<' }, { v: 'lte', l: '≤' },
  { v: 'between', l: 'between' }, { v: 'is_null', l: 'is empty' }, { v: 'is_not_null', l: 'is not empty' },
];
const NULL_OPS = new Set(['is_null', 'is_not_null']);
const MULTI_OPS = new Set(['in', 'not_in']);
const SUGGEST_OPS = new Set(['equals', 'not_equals', 'in', 'not_in']);
const PRESETS = [
  { v: 'today', l: 'Today' }, { v: 'yesterday', l: 'Yesterday' },
  { v: 'month_end', l: 'Month-end' }, { v: 'last_month_end', l: 'Last month-end' },
  { v: 'quarter_end', l: 'Quarter-end' }, { v: 'last_quarter_end', l: 'Last quarter-end' },
  { v: 'year_end', l: 'Year-end' }, { v: 'last_year_end', l: 'Last year-end' },
];
const isDateAttr = (a) => /date|time/i.test(String(a?.data_type || ''));

// ── Searchable single-select combobox over a list of {value,label} ──
function Combo({ value, onChange, options, placeholder = 'Select…', testid }) {
  const [open, setOpen] = useState(false);
  const sel = options.find(o => o.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between h-8 text-xs font-normal" data-testid={testid}>
          <span className="truncate">{sel ? sel.label : <span className="text-muted-foreground">{placeholder}</span>}</span>
          <ChevronsUpDown className="w-3 h-3 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
        <Command>
          <CommandInput placeholder="Search…" className="h-8 text-xs" />
          <CommandList>
            <CommandEmpty>No match.</CommandEmpty>
            <CommandGroup>
              {options.map(o => (
                <CommandItem key={o.value} value={o.label} onSelect={() => { onChange(o.value); setOpen(false); }} className="text-xs" data-testid={testid ? `${testid}-opt-${o.value}` : undefined}>
                  <Check className={`mr-2 h-3 w-3 ${o.value === value ? 'opacity-100' : 'opacity-0'}`} />
                  {o.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ── Condition value box with on-demand distinct-value suggestions + free text ──
function ValueCombo({ value, onChange, modelId, entityId, field, multi, testid }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [opts, setOpts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setLoaded(false); setOpts([]); }, [field, entityId]);
  const loadVals = async () => {
    if (loaded || !modelId || !entityId || !field) return;
    try { const r = await api.getCalcFieldValues(modelId, entityId, field); setOpts(r?.values || []); }
    catch { setOpts([]); }
    finally { setLoaded(true); }
  };
  const pick = (v) => {
    if (multi) {
      const cur = String(value || '').split(',').map(s => s.trim()).filter(Boolean);
      if (!cur.includes(v)) cur.push(v);
      onChange(cur.join(', '));
    } else { onChange(v); setOpen(false); }
    setQuery('');
  };
  const filtered = opts.filter(o => !query || o.toLowerCase().includes(query.toLowerCase())).slice(0, 50);
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) loadVals(); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="h-8 text-xs flex-1 justify-between font-normal" data-testid={testid}>
          <span className="truncate">{value ? value : <span className="text-muted-foreground">{multi ? 'values…' : 'value'}</span>}</span>
          <ChevronsUpDown className="w-3 h-3 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-64" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder={field ? `Search ${field}…` : 'Type a value…'} className="h-8 text-xs" value={query} onValueChange={setQuery} />
          <CommandList>
            {!loaded && <div className="px-2 py-2 text-[11px] text-muted-foreground">Loading values…</div>}
            {loaded && filtered.length === 0 && <CommandEmpty>No stored values.</CommandEmpty>}
            {query && (
              <CommandGroup heading="Custom">
                <CommandItem value={`__use_${query}`} onSelect={() => pick(query)} className="text-xs" data-testid={testid ? `${testid}-use` : undefined}>
                  Use “{query}”
                </CommandItem>
              </CommandGroup>
            )}
            {filtered.length > 0 && (
              <CommandGroup heading="From data">
                {filtered.map(o => (
                  <CommandItem key={o} value={o} onSelect={() => pick(o)} className="text-xs">
                    <Check className={`mr-2 h-3 w-3 ${String(value || '').split(',').map(s => s.trim()).includes(o) ? 'opacity-100' : 'opacity-0'}`} />
                    {o}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const emptyLeaf = () => ({ field: '', operator: 'equals', value: '' });
function normCondToUi(scope) {
  if (!scope) return { logic: 'and', conditions: [emptyLeaf()] };
  if (scope.bool && Array.isArray(scope.args)) {
    return { logic: scope.bool, conditions: scope.args.filter(a => a.field).map(a => ({ field: a.field, operator: a.operator, value: Array.isArray(a.value) ? a.value.join(', ') : (a.value ?? '') })) };
  }
  if (scope.field) return { logic: 'and', conditions: [{ field: scope.field, operator: scope.operator, value: Array.isArray(scope.value) ? scope.value.join(', ') : (scope.value ?? '') }] };
  return { logic: 'and', conditions: [emptyLeaf()] };
}
function uiCondToPayload(ui) {
  const args = (ui.conditions || []).filter(c => c.field).map(c => {
    const leaf = { field: c.field, operator: c.operator };
    if (!NULL_OPS.has(c.operator)) {
      if (c.operator === 'between') leaf.value = String(c.value).split(',').map(s => s.trim()).slice(0, 2);
      else if (c.operator === 'in' || c.operator === 'not_in') leaf.value = String(c.value).split(',').map(s => s.trim()).filter(Boolean);
      else leaf.value = c.value;
    }
    return leaf;
  });
  if (!args.length) return null;
  return { bool: ui.logic, args };
}

// ─────────────────────────── Create / edit dialog ───────────────────────────
function MeasureDialog({ open, onOpenChange, modelId, facts, editing, onSaved }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [aggregation, setAggregation] = useState('sum');
  const [baseField, setBaseField] = useState('');
  const [accumulation, setAccumulation] = useState('itd');
  const [asOfField, setAsOfField] = useState('');
  const [cond, setCond] = useState({ logic: 'and', conditions: [emptyLeaf()] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name || ''); setDescription(editing.description || '');
      setSourceId(editing.source_entity_id || ''); setAggregation(editing.aggregation || 'sum');
      setBaseField(editing.base_field || ''); setAccumulation(editing.accumulation || 'itd');
      setAsOfField(editing.as_of_field || ''); setCond(normCondToUi(editing.scope_cond));
    } else {
      setName(''); setDescription(''); setSourceId(facts[0]?.entity_id || '');
      setAggregation('sum'); setBaseField(''); setAccumulation('itd'); setAsOfField('');
      setCond({ logic: 'and', conditions: [emptyLeaf()] });
    }
  }, [open, editing, facts]);

  const fact = facts.find(f => f.entity_id === sourceId);
  const attrs = fact?.attributes || [];
  const fieldOpts = attrs.map(a => ({ value: a.name, label: a.name }));
  const dateOpts = attrs.filter(isDateAttr).map(a => ({ value: a.name, label: a.name }));

  // Default the as-of field from the fact's designated continuous date role.
  useEffect(() => {
    if (accumulation === 'itd' && !asOfField && fact) {
      setAsOfField((fact.date_roles || {}).as_of || fact.default_date_field || '');
    }
  }, [accumulation, fact, asOfField]);

  // Switching the canonical fact drops any column/date picks that don't exist
  // on the new fact (so a stale as-of field can't silently carry over).
  useEffect(() => {
    const names = new Set((fact?.attributes || []).map(a => a.name));
    if (baseField && !names.has(baseField)) setBaseField('');
    if (asOfField && !names.has(asOfField)) setAsOfField('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId]);

  const setLeaf = (i, patch) => setCond(c => ({ ...c, conditions: c.conditions.map((l, j) => j === i ? { ...l, ...patch } : l) }));

  const save = async () => {
    if (!name.trim()) { toast.error('Give the measure a name'); return; }
    if (!sourceId) { toast.error('Pick a canonical fact'); return; }
    if (aggregation !== 'count' && !baseField) { toast.error('Pick a column to aggregate'); return; }
    if (accumulation === 'itd' && !asOfField) { toast.error('Pick an as-of date field for inception-to-date'); return; }
    setSaving(true);
    const payload = { name: name.trim(), description, source_entity_id: sourceId, aggregation, base_field: baseField, accumulation, as_of_field: asOfField, condition: uiCondToPayload(cond) };
    try {
      if (editing) await api.updateCalcMeasure(editing.measure_id, payload);
      else await api.createCalcMeasure(modelId, payload);
      toast.success(editing ? 'Measure updated' : 'Measure created');
      onSaved(); onOpenChange(false);
    } catch (e) {
      toast.error(e?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-auto" data-testid="calc-measure-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit calculated measure' : 'New calculated measure'}</DialogTitle>
          <DialogDescription className="text-xs">Aggregate a column under a condition, once — reuse it to value the summary and to answer any as-of date.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-xs">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Name</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Direct Outstanding Loss Reserve" className="h-8 text-xs" data-testid="cm-name" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Canonical fact</Label>
              <Select value={sourceId} onValueChange={setSourceId}>
                <SelectTrigger className="h-8 text-xs" data-testid="cm-source"><SelectValue placeholder="Fact table" /></SelectTrigger>
                <SelectContent>{facts.map(f => <SelectItem key={f.entity_id} value={f.entity_id} className="text-xs">{f.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Aggregation</Label>
              <Select value={aggregation} onValueChange={setAggregation}>
                <SelectTrigger className="h-8 text-xs" data-testid="cm-agg"><SelectValue /></SelectTrigger>
                <SelectContent>{AGGS.map(a => <SelectItem key={a} value={a} className="text-xs">{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Column {aggregation === 'count' ? '(optional for count)' : ''}</Label>
              <Combo value={baseField} onChange={setBaseField} options={fieldOpts} placeholder="Pick column" testid="cm-basefield" />
            </div>
          </div>

          {/* Accumulation mode */}
          <div className="space-y-1.5">
            <Label className="text-xs">Accumulation</Label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setAccumulation('period')} className={`flex items-start gap-2 rounded-md border p-2 text-left transition-colors ${accumulation === 'period' ? 'border-primary bg-primary/10' : 'border-border/60 hover:bg-muted/40'}`} data-testid="cm-acc-period">
                <CalendarClock className="w-4 h-4 mt-0.5 shrink-0" />
                <span><span className="font-medium">Period</span><br /><span className="text-muted-foreground">Sums within the period the query asks for.</span></span>
              </button>
              <button type="button" onClick={() => setAccumulation('itd')} className={`flex items-start gap-2 rounded-md border p-2 text-left transition-colors ${accumulation === 'itd' ? 'border-primary bg-primary/10' : 'border-border/60 hover:bg-muted/40'}`} data-testid="cm-acc-itd">
                <InfinityIcon className="w-4 h-4 mt-0.5 shrink-0" />
                <span><span className="font-medium">Inception-to-date</span><br /><span className="text-muted-foreground">Cumulative from the beginning up to the as-of date.</span></span>
              </button>
            </div>
            {accumulation === 'itd' && (
              <div className="space-y-1 pt-1">
                <Label className="text-xs">As-of date field (continuous boundary)</Label>
                <Combo value={asOfField} onChange={setAsOfField} options={dateOpts.length ? dateOpts : fieldOpts} placeholder="Pick a date field" testid="cm-asoffield" />
              </div>
            )}
          </div>

          {/* Condition builder */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Condition</Label>
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">match</span>
                <Select value={cond.logic} onValueChange={(v) => setCond(c => ({ ...c, logic: v }))}>
                  <SelectTrigger className="h-7 w-20 text-xs" data-testid="cm-logic"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="and" className="text-xs">all (AND)</SelectItem><SelectItem value="or" className="text-xs">any (OR)</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5" data-testid="cm-conditions">
              {cond.conditions.map((leaf, i) => (
                <div key={i} className="flex items-center gap-1.5" data-testid={`cm-rule-${i}`}>
                  <div className="w-[38%]"><Combo value={leaf.field} onChange={(v) => setLeaf(i, { field: v })} options={fieldOpts} placeholder="field" testid={`cm-rule-field-${i}`} /></div>
                  <Select value={leaf.operator} onValueChange={(v) => setLeaf(i, { operator: v })}>
                    <SelectTrigger className="h-8 w-[22%] text-xs" data-testid={`cm-rule-op-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{OPERATORS.map(o => <SelectItem key={o.v} value={o.v} className="text-xs">{o.l}</SelectItem>)}</SelectContent>
                  </Select>
                  {!NULL_OPS.has(leaf.operator) && (
                    SUGGEST_OPS.has(leaf.operator) ? (
                      <ValueCombo value={leaf.value} onChange={(v) => setLeaf(i, { value: v })} modelId={modelId} entityId={sourceId} field={leaf.field} multi={MULTI_OPS.has(leaf.operator)} testid={`cm-rule-val-${i}`} />
                    ) : (
                      <Input value={leaf.value} onChange={(e) => setLeaf(i, { value: e.target.value })} placeholder={leaf.operator === 'between' ? 'lo, hi' : 'value'} className="h-8 text-xs flex-1" data-testid={`cm-rule-val-${i}`} />
                    )
                  )}
                  <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => setCond(c => ({ ...c, conditions: c.conditions.filter((_, j) => j !== i) }))} data-testid={`cm-rule-del-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => setCond(c => ({ ...c, conditions: [...c.conditions, emptyLeaf()] }))} data-testid="cm-add-rule"><Plus className="w-3 h-3" />Add rule</Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="cm-save">{saving ? 'Saving…' : (editing ? 'Save changes' : 'Create measure')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────── Evaluate + movement ───────────────────────────
function SourceBadge({ source }) {
  return (
    <Badge variant="outline" className={`text-[9px] h-4 px-1 gap-0.5 ${source === 'summary' ? 'border-emerald-500/40 text-emerald-400' : 'border-amber-500/40 text-amber-400'}`}>
      {source === 'summary' ? <Database className="w-2.5 h-2.5" /> : <InfinityIcon className="w-2.5 h-2.5" />}{source}
    </Badge>
  );
}

function EvaluateRow({ measure }) {
  const [asOf, setAsOf] = useState('');
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showMove, setShowMove] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [mv, setMv] = useState(null);
  const [mvBusy, setMvBusy] = useState(false);
  const isItd = measure.accumulation === 'itd';

  const runEval = async (body) => {
    setBusy(true);
    try { setRes(await api.evaluateCalcMeasure(measure.measure_id, body)); }
    catch (e) { toast.error(e?.message || 'Evaluate failed'); }
    finally { setBusy(false); }
  };
  const runMovement = async () => {
    if (!from || !to) { toast.error('Pick a start and end date'); return; }
    setMvBusy(true);
    try { setMv(await api.evaluateMovement(measure.measure_id, { from_date: from, to_date: to })); }
    catch (e) { toast.error(e?.message || 'Movement failed'); }
    finally { setMvBusy(false); }
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 flex-wrap">
        <Input type="date" value={asOf} onChange={e => setAsOf(e.target.value)} className="h-7 w-36 text-xs" data-testid={`cm-asof-${measure.measure_id}`} />
        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => runEval(asOf ? { as_of_date: asOf } : {})} disabled={busy} data-testid={`cm-eval-${measure.measure_id}`}><PlayCircle className="w-3 h-3" />{busy ? '…' : 'Evaluate'}</Button>
        <Select value="" onValueChange={(p) => { setAsOf(''); runEval({ as_of_preset: p }); }}>
          <SelectTrigger className="h-7 w-28 text-xs" data-testid={`cm-preset-${measure.measure_id}`}><SelectValue placeholder="Quick pick" /></SelectTrigger>
          <SelectContent>{PRESETS.map(p => <SelectItem key={p.v} value={p.v} className="text-xs">{p.l}</SelectItem>)}</SelectContent>
        </Select>
        {res && (
          <span className="text-xs flex items-center gap-1.5" data-testid={`cm-eval-result-${measure.measure_id}`}>
            <span className="font-semibold tabular-nums">{res.value == null ? '—' : Number(res.value).toLocaleString()}</span>
            <SourceBadge source={res.source} />
            {res.as_of_date && <span className="text-muted-foreground">@ {res.as_of_date}{res.preset ? ` · ${(PRESETS.find(p => p.v === res.preset) || {}).l || res.preset}` : ''}</span>}
          </span>
        )}
      </div>
      {isItd && (
        <div>
          <button type="button" onClick={() => setShowMove(s => !s)} className="text-[10px] text-primary hover:underline flex items-center gap-1" data-testid={`cm-move-toggle-${measure.measure_id}`}>
            <GitCompareArrows className="w-3 h-3" />{showMove ? 'Hide movement' : 'Movement in period'}
          </button>
          {showMove && (
            <div className="flex items-center gap-1.5 flex-wrap mt-1 pl-1">
              <Input type="date" value={from} onChange={e => setFrom(e.target.value)} className="h-7 w-32 text-xs" data-testid={`cm-move-from-${measure.measure_id}`} />
              <span className="text-[10px] text-muted-foreground">→</span>
              <Input type="date" value={to} onChange={e => setTo(e.target.value)} className="h-7 w-32 text-xs" data-testid={`cm-move-to-${measure.measure_id}`} />
              <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={runMovement} disabled={mvBusy} data-testid={`cm-move-run-${measure.measure_id}`}>{mvBusy ? '…' : 'Compute'}</Button>
              {mv && (
                <span className="text-xs flex items-center gap-1.5" data-testid={`cm-move-result-${measure.measure_id}`}>
                  <span className={`font-semibold tabular-nums ${mv.movement > 0 ? 'text-emerald-400' : mv.movement < 0 ? 'text-red-400' : ''}`}>{mv.movement == null ? '—' : (mv.movement > 0 ? '+' : '') + Number(mv.movement).toLocaleString()}</span>
                  <span className="text-muted-foreground">({Number(mv.from.value).toLocaleString()} → {Number(mv.to.value).toLocaleString()})</span>
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── Main panel ───────────────────────────
export function CalculatedMeasuresPanel({ modelId }) {
  const [entities, setEntities] = useState([]);
  const [aggregates, setAggregates] = useState([]);
  const [measures, setMeasures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [reconcile, setReconcile] = useState({});

  const load = useCallback(async () => {
    if (!modelId) return;
    setLoading(true);
    try {
      const [ents, aggs, ms] = await Promise.all([
        api.getSemanticEntities(modelId), api.listAggregatesForModel(modelId), api.listCalcMeasures(modelId),
      ]);
      setEntities(Array.isArray(ents) ? ents : (ents?.entities || []));
      setAggregates(Array.isArray(aggs) ? aggs : (aggs?.aggregates || []));
      setMeasures(Array.isArray(ms) ? ms : []);
    } catch (e) { toast.error('Failed to load calculated measures'); }
    finally { setLoading(false); }
  }, [modelId]);
  useEffect(() => { load(); }, [load]);

  const facts = useMemo(() => entities.filter(e => e.entity_type === 'fact' && !e.derived_from_entity), [entities]);
  const factById = useMemo(() => Object.fromEntries(entities.map(e => [e.entity_id, e])), [entities]);

  const saveDateRoles = async (entity_id, as_of_field, snapshot_field) => {
    try { await api.setDateRoles(modelId, { entity_id, as_of_field, snapshot_field }); toast.success('Date roles saved'); load(); }
    catch (e) { toast.error(e?.message || 'Save failed'); }
  };
  const delMeasure = async (m) => {
    if (!window.confirm(`Delete "${m.name}"?`)) return;
    try { await api.deleteCalcMeasure(m.measure_id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.message || 'Delete failed'); }
  };
  const runReconcile = async (entityId) => {
    try { const r = await api.reconcileItd(entityId); setReconcile(s => ({ ...s, [entityId]: r })); const n = (r.results || []).reduce((a, x) => a + (x.drift_count || 0), 0); toast[n ? 'warning' : 'success'](n ? `${n} drift(s) found` : 'All snapshots match'); }
    catch (e) { toast.error(e?.message || 'Reconcile failed'); }
  };
  const [bulk, setBulk] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const runBulk = async () => {
    setBulkBusy(true);
    try { const r = await api.reconcileItdAll(modelId); setBulk(r); toast[r.total_drift ? 'warning' : 'success'](r.total_drift ? `${r.total_drift} drift(s) across ${r.summaries.length} summaries` : `All ${r.linked_measures} linked measures match`); }
    catch (e) { toast.error(e?.message || 'Reconcile failed'); }
    finally { setBulkBusy(false); }
  };

  if (!modelId) return <p className="text-xs text-muted-foreground/70 italic p-3">Save the model to manage calculated measures</p>;

  return (
    <div className="p-3 space-y-4 text-xs" data-testid="itd-panel">
      {/* Date roles */}
      <section className="space-y-2">
        <h3 className="font-semibold text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" />Canonical date roles</h3>
        <p className="text-[11px] text-muted-foreground">Designate each fact's continuous as-of date and its month-end snapshot date.</p>
        <div className="space-y-2">
          {facts.map(f => {
            const dates = (f.attributes || []).filter(isDateAttr);
            const roles = f.date_roles || {};
            return (
              <div key={f.entity_id} className="rounded-md border border-border/50 p-2 space-y-1.5" data-testid={`dateroles-${f.entity_id}`}>
                <div className="font-medium">{f.name}</div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-[10px] text-muted-foreground">As-of (continuous)</Label>
                    <Select value={roles.as_of || '__none'} onValueChange={(v) => saveDateRoles(f.entity_id, v === '__none' ? '' : v, roles.snapshot || '')}>
                      <SelectTrigger className="h-7 text-xs" data-testid={`dr-asof-${f.entity_id}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent><SelectItem value="__none" className="text-xs">—</SelectItem>{dates.map(a => <SelectItem key={a.name} value={a.name} className="text-xs">{a.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-muted-foreground">Snapshot (month-end)</Label>
                    <Select value={roles.snapshot || '__none'} onValueChange={(v) => saveDateRoles(f.entity_id, roles.as_of || '', v === '__none' ? '' : v)}>
                      <SelectTrigger className="h-7 text-xs" data-testid={`dr-snap-${f.entity_id}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent><SelectItem value="__none" className="text-xs">—</SelectItem>{dates.map(a => <SelectItem key={a.name} value={a.name} className="text-xs">{a.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Calculated measures */}
      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><InfinityIcon className="w-3.5 h-3.5" />Calculated measures</h3>
          <Button size="sm" className="h-7 text-xs gap-1" onClick={() => { setEditing(null); setDlgOpen(true); }} data-testid="cm-new"><Plus className="w-3 h-3" />New</Button>
        </div>
        {loading ? <p className="text-muted-foreground">Loading…</p> : measures.length === 0 ? (
          <p className="text-muted-foreground/70 italic">No calculated measures yet. Create one to express an inception-to-date figure from the transaction fact.</p>
        ) : (
          <div className="space-y-2">
            {measures.map(m => (
              <div key={m.measure_id} className="rounded-md border border-border/50 p-2 space-y-1.5" data-testid={`cm-row-${m.measure_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{m.name}</div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {m.aggregation}({m.base_field || '*'}) · {factById[m.source_entity_id]?.name || m.source_entity_id}
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    <Badge variant="outline" className={`text-[9px] h-4 px-1 ${m.accumulation === 'itd' ? 'border-primary/40 text-primary' : 'border-border/60 text-muted-foreground'}`}>{m.accumulation === 'itd' ? 'ITD' : 'period'}</Badge>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setEditing(m); setDlgOpen(true); }} data-testid={`cm-edit-${m.measure_id}`}><Pencil className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => delMeasure(m)} data-testid={`cm-del-${m.measure_id}`}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
                <EvaluateRow measure={m} />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Summaries: valuation column + links + reconcile */}
      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><GitCompareArrows className="w-3.5 h-3.5" />Summary tables</h3>
          {aggregates.length > 0 && (
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={runBulk} disabled={bulkBusy} data-testid="reconcile-all"><GitCompareArrows className="w-3 h-3" />{bulkBusy ? 'Reconciling…' : 'Reconcile all'}</Button>
          )}
        </div>
        {bulk && (
          <div className={`rounded-md border p-2 text-xs ${bulk.total_drift ? 'border-red-500/40 bg-red-500/5' : 'border-emerald-500/40 bg-emerald-500/5'}`} data-testid="bulk-reconcile-summary">
            <div className="flex items-center justify-between">
              <span className="font-medium">{bulk.total_drift ? `${bulk.total_drift} drift(s) found` : 'All snapshots match'}</span>
              <span className="text-muted-foreground">{bulk.linked_measures} linked · {bulk.summaries.length} summaries</span>
            </div>
            {bulk.summaries.map(s => (
              <div key={s.entity_id} className="flex items-center justify-between mt-1">
                <span className="truncate">{s.summary_name}</span>
                <Badge variant="outline" className={`text-[9px] h-4 px-1 ${s.drift_count ? 'border-red-500/40 text-red-400' : 'border-emerald-500/40 text-emerald-400'}`}>{s.drift_count ? `${s.drift_count} drift` : 'match'}</Badge>
              </div>
            ))}
          </div>
        )}
        {aggregates.length === 0 ? (
          <p className="text-muted-foreground/70 italic">No summary (aggregate) tables on this model.</p>
        ) : aggregates.map(a => {
          const canonical = factById[a.derived_from_entity];
          const boundEnt = factById[a.summary_entity_id] || factById[a.entity_id] || canonical;
          const colOpts = (boundEnt?.attributes || []);
          const dateCols = colOpts.filter(isDateAttr);
          const mmFields = Object.keys(a.measure_map || {});
          const linkable = measures.filter(m => !a.derived_from_entity || m.source_entity_id === a.derived_from_entity);
          const rec = reconcile[a.entity_id];
          return (
            <div key={a.entity_id} className="rounded-md border border-border/50 p-2 space-y-2" data-testid={`summary-${a.entity_id}`}>
              <div className="font-medium">{a.name || a.table_name}</div>
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground">Valuation-date column</Label>
                <Select value={a.valuation_date_column || '__none'} onValueChange={async (v) => { try { await api.setValuationConfig(a.entity_id, v === '__none' ? '' : v); toast.success('Valuation column set'); load(); } catch (e) { toast.error(e?.message || 'Save failed'); } }}>
                  <SelectTrigger className="h-7 text-xs" data-testid={`val-col-${a.entity_id}`}><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="__none" className="text-xs">—</SelectItem>{(dateCols.length ? dateCols : colOpts).map(c => <SelectItem key={c.name} value={c.name} className="text-xs">{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {mmFields.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">Link summary fields → calculated measure</Label>
                  {mmFields.map(f => {
                    const spec = a.measure_map[f] || {};
                    return (
                      <div key={f} className="flex items-center gap-1.5">
                        <span className="w-[42%] truncate font-mono text-[10px]" title={f}>{f}</span>
                        <Select value={spec.calc_measure_id || '__none'} onValueChange={async (v) => { try { await api.linkCalcMeasure(a.entity_id, f, v === '__none' ? '' : v); toast.success('Link updated'); load(); } catch (e) { toast.error(e?.message || 'Link failed'); } }}>
                          <SelectTrigger className="h-7 flex-1 text-xs" data-testid={`link-${a.entity_id}-${f}`}><SelectValue placeholder="— not linked —" /></SelectTrigger>
                          <SelectContent><SelectItem value="__none" className="text-xs">— not linked —</SelectItem>{linkable.map(m => <SelectItem key={m.measure_id} value={m.measure_id} className="text-xs">{m.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
              )}
              <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => runReconcile(a.entity_id)} disabled={!a.valuation_date_column} data-testid={`reconcile-${a.entity_id}`}><GitCompareArrows className="w-3 h-3" />Reconcile snapshots</Button>
              {rec && (rec.results || []).map(r => (
                <div key={r.summary_field} className="rounded border border-border/40 p-1.5 space-y-1" data-testid={`recon-${a.entity_id}-${r.summary_field}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px]">{r.summary_field} → {r.calc_measure}</span>
                    <Badge variant="outline" className={`text-[9px] h-4 px-1 ${r.drift_count ? 'border-red-500/40 text-red-400' : 'border-emerald-500/40 text-emerald-400'}`}>{r.drift_count ? `${r.drift_count} drift` : 'match'}</Badge>
                  </div>
                  <table className="w-full text-[10px]">
                    <thead><tr className="text-muted-foreground"><th className="text-left font-normal">valuation date</th><th className="text-right font-normal">stored</th><th className="text-right font-normal">recomputed</th><th className="text-right font-normal">diff</th></tr></thead>
                    <tbody>{r.rows.map(row => (
                      <tr key={row.valuation_date} className={row.drift ? 'text-red-400' : ''}>
                        <td>{row.valuation_date}</td>
                        <td className="text-right tabular-nums">{row.stored?.toLocaleString() ?? '—'}</td>
                        <td className="text-right tabular-nums">{row.recomputed?.toLocaleString() ?? '—'}</td>
                        <td className="text-right tabular-nums">{row.difference?.toLocaleString() ?? '—'}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              ))}
            </div>
          );
        })}
      </section>

      <MeasureDialog open={dlgOpen} onOpenChange={setDlgOpen} modelId={modelId} facts={facts} editing={editing} onSaved={load} />
    </div>
  );
}
